import { Info } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

/**
 * Inline info popover that explains what the Health Score is, what feeds into
 * it and how to use it. Dropped next to any "score" UI on the dashboard.
 *
 * Props:
 *   - size:   'sm' | 'md'  (icon size; default md)
 *   - label:  optional trigger text shown next to the icon (default: 'About this score')
 *   - inline: if true, renders the trigger without the rounded-pill border
 */
export default function HealthScoreExplainer({ size = 'md', label = 'About this score', inline = false }) {
  const iconSize = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="About the health score"
          data-testid="health-score-explainer-trigger"
          className={cn(
            'inline-flex items-center gap-1.5 text-xs text-[#5D524D] hover:text-[#2C2420] transition-colors',
            !inline && 'px-2 py-1 rounded-full border border-[#E6E0D9] hover:border-[#4A3B32]/40 bg-white'
          )}
        >
          <Info className={iconSize} />
          {label && <span className="hidden sm:inline">{label}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 p-0 border-[#E6E0D9] shadow-lg"
        data-testid="health-score-explainer-popover"
      >
        <div className="p-4 border-b border-[#E6E0D9]">
          <p className="font-heading text-sm text-[#2C2420] mb-1">What is the Health Score?</p>
          <p className="text-xs text-[#5D524D] leading-relaxed">
            A single number, 0&ndash;100, summarising how well each client relationship is being
            managed across data, compliance, tasks, sources and recent activity.
          </p>
        </div>

        <div className="p-4 border-b border-[#E6E0D9]">
          <p className="text-[11px] uppercase tracking-wide text-[#8C8078] mb-2">What goes into it</p>
          <ul className="space-y-1.5 text-xs text-[#2C2420]">
            <li className="flex items-baseline gap-2">
              <span className="text-[#8C8078] w-10 shrink-0">25%</span>
              <span><strong>Data completeness</strong> &mdash; individuals, entities &amp; roles populated</span>
            </li>
            <li className="flex items-baseline gap-2">
              <span className="text-[#8C8078] w-10 shrink-0">20%</span>
              <span><strong>Compliance</strong> &mdash; governance docs current, no expired items</span>
            </li>
            <li className="flex items-baseline gap-2">
              <span className="text-[#8C8078] w-10 shrink-0">20%</span>
              <span><strong>Task health</strong> &mdash; nothing slipping, low overdue count</span>
            </li>
            <li className="flex items-baseline gap-2">
              <span className="text-[#8C8078] w-10 shrink-0">15%</span>
              <span><strong>Link freshness</strong> &mdash; source links indexed and recently checked</span>
            </li>
            <li className="flex items-baseline gap-2">
              <span className="text-[#8C8078] w-10 shrink-0">20%</span>
              <span><strong>Activity recency</strong> &mdash; meetings, file work &amp; portal contact</span>
            </li>
          </ul>
        </div>

        <div className="p-4 border-b border-[#E6E0D9]">
          <p className="text-[11px] uppercase tracking-wide text-[#8C8078] mb-2">How to read it</p>
          <div className="flex items-center justify-between text-[11px] gap-1">
            <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-medium">85+&nbsp;Excellent</span>
            <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">70&ndash;84&nbsp;Good</span>
            <span className="px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 font-medium">50&ndash;69&nbsp;Attention</span>
            <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-medium">&lt;50&nbsp;Critical</span>
          </div>
        </div>

        <div className="p-4">
          <p className="text-[11px] uppercase tracking-wide text-[#8C8078] mb-1">Why it&rsquo;s here</p>
          <p className="text-xs text-[#5D524D] leading-relaxed">
            Surfaces which clients need attention <em>before</em> issues escalate. Click any client
            card to drill into the breakdown and the recommended next actions.
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
